import { z } from 'zod';
import { FINANCIAL_PRODUCT } from '../constants';
import { requiredString } from '../helper';
import {
  address,
  baseResidentialApplicant,
  generateSchema,
  repaymentTerms,
  termsConditionsAndAgreements,
} from '../shared';
import { ApplicationStep } from '../types';
import { baseApplicationStep } from './baseApplication';

/**
 * Schema to validate the create application payload
 * bgl & bpl will use default loan terms
 * act shs, tas esls require additional gov terms as well as unsolicited sale options
 * act slip has same requirement as act shs / tas esls but without haveAustralianIncome
 * act sfa requires business terms
 * flex requires payment plan terms as well as its own flex specific terms
 *
 */

const baseLoanTerms = termsConditionsAndAgreements.pick({
  beOver18: true,
  haveAustralianIncome: true,
  haveFilledOwnDetails: true,
  consentPrivacyPolicy: true,
  consentCreditReport: true,
  consentCustomerDeclaration: true,
  creditAndTermsAgreement: true,
  haveReadDisclosureOfCreditInformation: true,
});

const govLoanTerms = termsConditionsAndAgreements.pick({
  beGovSchemeHomeOwner: true,
  consentGovSchemeTerms: true,
  haveReadDisclosureOfCreditInformation: true,
});

const businessTermsAndConditions = termsConditionsAndAgreements.pick({
  businessEligibilityCriteriaAgreement: true,
  consentCustomerDeclaration: true,
});

const basePaymentPlan = termsConditionsAndAgreements.pick({
  beOver18: true,
  haveAustralianIncome: true,
  consentPrivacyPolicy: true,
  consentCustomerDeclaration: true,
  creditAndTermsAgreement: true,
  haveReadDisclosureOfCreditInformation: true,
});

const flexPaymentPlan = termsConditionsAndAgreements.pick({
  haveActiveMortgage: true,
  haveEligibleNetIncome: true,
  consentCreditCheck: true,
  haveReadDisclosureOfCreditInformation: true,
});

const electrify2515Loan = termsConditionsAndAgreements.pick({
  enterprisePartnerTerms: true,
  beOver18: true,
  haveAustralianIncome: true,
  haveFilledOwnDetails: true,
  consentPrivacyPolicy: true,
  consentCustomerDeclaration: true,
  creditAndTermsAgreement: true,
  haveReadDisclosureOfCreditInformation: true,
});

const defaultTermsSchema = {
  applicant: z.object({
    termsAndConditions: baseLoanTerms,
  }),
};

const actSlipTerms = {
  applicant: z.object({
    termsAndConditions: baseLoanTerms.merge(govLoanTerms).omit({ haveAustralianIncome: true }),
  }),
};

const brighteFlexTerms = {
  applicant: z.object({
    termsAndConditions: basePaymentPlan.merge(flexPaymentPlan),
  }),
};

const tasTerms = {
  applicant: z.object({
    termsAndConditions: baseLoanTerms.merge(govLoanTerms),
  }),
};

const actShsTerms = {
  applicant: z.object({
    termsAndConditions: baseLoanTerms.merge(govLoanTerms).merge(
      termsConditionsAndAgreements.pick({
        beCompletedPropertyOwner: true,
      }),
    ),
  }),
};

const actSfaTerms = {
  applicant: z.object({
    termsAndConditions: businessTermsAndConditions,
  }),
};

const smeTerms = {
  applicant: z.object({
    termsAndConditions: businessTermsAndConditions,
  }),
};

const electrify2515Terms = {
  applicant: z.object({
    termsAndConditions: electrify2515Loan,
  }),
};

const brightePayTerms = {
  applicant: z.object({
    termsAndConditions: baseLoanTerms.omit({
      haveFilledOwnDetails: true,
      consentCreditReport: true,
    }),
    unsolicitedSaleOptions: z.string().optional(),
    //TODO: make this mandatory later
    estimatedInstallationPeriod: z.string().optional(),
  }),
};

const customTermsSchemas = {
  [FINANCIAL_PRODUCT.ACT_SLIP]: actSlipTerms,
  [FINANCIAL_PRODUCT.FLEX]: brighteFlexTerms,
  [FINANCIAL_PRODUCT.TAS_ESLS]: tasTerms,
  [FINANCIAL_PRODUCT.ACT_SHS]: actShsTerms,
  [FINANCIAL_PRODUCT.ACT_SFA]: actSfaTerms,
  [FINANCIAL_PRODUCT.ELECTRIFY_2515]: electrify2515Terms,
  [FINANCIAL_PRODUCT.BRIGHTE_PAY]: brightePayTerms,
  [FINANCIAL_PRODUCT.SME]: smeTerms,
};

export const termsAndConditionsSchema = generateSchema<typeof customTermsSchemas, typeof defaultTermsSchema>({
  customSchemas: customTermsSchemas,
  defaultSchema: defaultTermsSchema,
});

const defaultOtherApplicantPropertiesSchema = {
  applicant: z.object({
    ownProperty: z.boolean(),
    nonHomeOwnerCheck: z.boolean().optional(),
    isCurrentAddress: z.boolean().optional(),
    previousAddress: address.optional(),
  }),
};

const actSlipShsAndTasOtherApplicantProperties = {
  applicant: z.object({
    unsolicitedSaleOptions: requiredString(),
    //TODO: make this mandatory later
    estimatedInstallationPeriod: z.string().optional(),
    ownProperty: z.boolean(),
    nonHomeOwnerCheck: z.boolean().optional(),
  }),
};

const actSfaOtherApplicantProperties = {
  applicant: z.object({
    ownProperty: z.undefined(),
    nonHomeOwnerCheck: z.boolean().optional(),
  }),
};

const smeOtherApplicantProperties = {
  applicant: z.object({
    ownProperty: z.undefined(),
    nonHomeOwnerCheck: z.boolean().optional(),
  }),
};

const otherApplicantPropertiesCustomSchemas = {
  [FINANCIAL_PRODUCT.ACT_SLIP]: actSlipShsAndTasOtherApplicantProperties,
  [FINANCIAL_PRODUCT.TAS_ESLS]: actSlipShsAndTasOtherApplicantProperties,
  [FINANCIAL_PRODUCT.ACT_SHS]: actSlipShsAndTasOtherApplicantProperties,
  [FINANCIAL_PRODUCT.ACT_SFA]: actSfaOtherApplicantProperties,
  [FINANCIAL_PRODUCT.SME]: smeOtherApplicantProperties,
};

export const otherApplicantPropertiesSchema = generateSchema<
  typeof otherApplicantPropertiesCustomSchemas,
  typeof defaultOtherApplicantPropertiesSchema
>({
  customSchemas: otherApplicantPropertiesCustomSchemas,
  defaultSchema: defaultOtherApplicantPropertiesSchema,
});

const customCreateApplicationSchema = {
  [FINANCIAL_PRODUCT.ELECTRIFY_2515]: {
    payment: z.object({
      isCashApplication: z.boolean(),
    }),
  },
};

const defaultCreateApplicationSchema = {};

export const createApplicationSchema = generateSchema<
  typeof customCreateApplicationSchema,
  typeof defaultCreateApplicationSchema
>({
  customSchemas: customCreateApplicationSchema,
  defaultSchema: defaultCreateApplicationSchema,
})
  .and(baseApplicationStep)
  .and(
    z
      .object({
        step: z.literal(ApplicationStep.CreateApplication),
        address: address,
        repaymentTerm: repaymentTerms,
        applicant: baseResidentialApplicant.pick({
          type: true,
          firstName: true,
          middleName: true,
          lastName: true,
          email: true,
          phone: true,
          secureCode: true,
        }),
      })
      .and(termsAndConditionsSchema)
      .and(otherApplicantPropertiesSchema),
  );
